/**
 * Preset lists carried over verbatim from the previous editor (photo-editor.component.tsx, 2026-09),
 * plus a YouTube group. Percent and by-height presets drive Resize; aspect ratios drive Crop;
 * platform sizes are offered by both (Resize fits inside the box, Crop takes the aspect and the
 * exact pixels — see model/edit-stack.ts setResizeBox / setCropToPlatformSize).
 */
import type { Size } from './model/edit-stack';

export const PERCENT_PRESETS = [25, 50, 75, 150, 200] as const;

/** Usage-QA J14-5 (2026-10-07): the common screen/video heights only. The old list (170p, 315p, 396p, 566p,
 *  627p, 628p, 851p…) was the social sizes' leftovers; those live in the platform groups below. */
export const HEIGHT_PRESETS = [240, 360, 480, 720, 1080, 1440, 2160] as const;

/** Usage-QA J9-4: square icon sizes for ICO pages (the /resize-ico subtitle promises 16 to 64; ICO holds up to 256). */
export const ICON_PRESETS = [16, 32, 48, 64, 128, 256] as const;

/** IMAGEEDITOR-03a (review 10): "Long edge N" = fit within N x N, aspect kept (setResizeBox with the lock on), so a
 *  mixed landscape / portrait batch gets max(w, h) = N on every file. No don't-enlarge clamp (owner, 2026-09-07). */
export const LONG_EDGE_PRESETS = [1080, 1600, 2048, 3840] as const;

export interface AspectPreset { label: string; value: number | 'original' | null; }
export const ASPECT_PRESETS: AspectPreset[] = [
    { label: 'Free', value: null },
    { label: 'Original', value: 'original' },
    { label: '1:1', value: 1 },
    // IMAGEEDITOR-F2 (review 10): the portrait ratios people crop to (4:5 feed, 9:16 stories, 3:4, 2:3 pins / prints)
    { label: '4:5', value: 4 / 5 },
    { label: '4:3', value: 4 / 3 },
    { label: '3:4', value: 3 / 4 },
    { label: '3:2', value: 3 / 2 },
    { label: '2:3', value: 2 / 3 },
    { label: '16:9', value: 16 / 9 },
    { label: '9:16', value: 9 / 16 },
    { label: '16:10', value: 16 / 10 },
    { label: '2:1', value: 2 },
];

export interface PlatformSize extends Size { label: string; }
export interface PlatformGroup { name: string; sizes: PlatformSize[]; }

export const PLATFORM_GROUPS: PlatformGroup[] = [
    { name: 'Facebook', sizes: [
        { label: 'Profile photo', width: 170, height: 170 }, { label: 'Landscape', width: 1200, height: 628 },
        { label: 'Portrait', width: 628, height: 1200 }, { label: 'Square', width: 1200, height: 1200 },
        { label: 'Stories', width: 1080, height: 1920 }, { label: 'Cover photo', width: 851, height: 315 } ] },
    { name: 'Instagram', sizes: [
        { label: 'Profile photo', width: 320, height: 320 }, { label: 'Landscape', width: 1080, height: 566 },
        { label: 'Portrait', width: 1080, height: 1350 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Stories', width: 1080, height: 1920 } ] },
    { name: 'X / Twitter', sizes: [
        { label: 'Profile photo', width: 400, height: 400 }, { label: 'Landscape', width: 1600, height: 900 },
        { label: 'Portrait', width: 1080, height: 1350 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Cover photo', width: 1500, height: 500 } ] },
    { name: 'LinkedIn', sizes: [
        { label: 'Profile photo', width: 400, height: 400 }, { label: 'Landscape', width: 1200, height: 627 },
        { label: 'Portrait', width: 627, height: 1200 }, { label: 'Square', width: 1080, height: 1080 },
        { label: 'Cover photo', width: 1584, height: 396 } ] },
    { name: 'TikTok', sizes: [
        { label: 'Profile photo', width: 200, height: 200 }, { label: 'Landscape', width: 1920, height: 1080 },
        { label: 'Portrait', width: 1080, height: 1920 }, { label: 'Square', width: 1080, height: 1080 } ] },
    { name: 'YouTube', sizes: [
        { label: 'Thumbnail', width: 1280, height: 720 }, { label: 'Channel banner', width: 2560, height: 1440 },
        { label: 'Profile photo', width: 800, height: 800 } ] },
    // IMAGEEDITOR-F2 (review 10): Pinterest's recommended 2:3 pin
    { name: 'Pinterest', sizes: [
        { label: 'Pin', width: 1000, height: 1500 } ] },
];

export function platformSizeLabel(g: PlatformGroup, s: PlatformSize): string {
    return `${s.width} × ${s.height} · ${s.label}`;
}
